import { QUOTATION_REGION_OPTIONS, getCurrencyForRegion } from "./quotationConstants";
import { DeviceCategory } from "models/quotation";

export interface SellableDeviceCategoryOption {
  value: string;
  label: string;
  badgeBg: string;
  badgeColor: string;
  badgeClass: string;
}

export const SELLABLE_DEVICE_CATEGORY_OPTIONS: SellableDeviceCategoryOption[] = [
  { value: "KDS", label: "Kitchen Display System (KDS)", badgeBg: "#ecfff6", badgeColor: "#009b72", badgeClass: "sellable-device-badge--kds" },
  { value: "POS", label: "POS Terminal / Screen", badgeBg: "#eef2ff", badgeColor: "#4f46e5", badgeClass: "sellable-device-badge--pos" },
  { value: "PRINTER", label: "Receipt / Kitchen Printer", badgeBg: "#eff6ff", badgeColor: "#2563eb", badgeClass: "sellable-device-badge--printer" },
  { value: "TABLET", label: "Tablet / Handheld", badgeBg: "#fdf4ff", badgeColor: "#a21caf", badgeClass: "sellable-device-badge--tablet" },
  { value: "SCANNER", label: "Barcode / QR Scanner", badgeBg: "#fff7ed", badgeColor: "#c2410c", badgeClass: "sellable-device-badge--scanner" },
  { value: "KIOSK", label: "Self-Service Kiosk", badgeBg: "#f0fdf4", badgeColor: "#15803d", badgeClass: "sellable-device-badge--kiosk" },
  { value: "PAYMENT_TERMINAL", label: "Payment / EDC Terminal", badgeBg: "#fefce8", badgeColor: "#a16207", badgeClass: "sellable-device-badge--payment" },
  { value: "ACCESSORY", label: "Cash Drawer / Stand / Mount", badgeBg: "#f1f5f9", badgeColor: "#475569", badgeClass: "sellable-device-badge--accessory" },
  { value: "OTHER", label: "Other Hardware", badgeBg: "#f8fafc", badgeColor: "#64748b", badgeClass: "sellable-device-badge--other" },
];

export const SELLABLE_DEVICE_COST_TYPE_OPTIONS = [
  { value: "ONE_TIME", label: "One-Time Cost (Hardware / Setup)" },
  { value: "RENEWAL", label: "Recurring / Rental Cost (Renewal)" },
];

export const SELLABLE_DEVICE_FORM_CATEGORY_OPTIONS = [
  ...SELLABLE_DEVICE_CATEGORY_OPTIONS.map((cat) => ({
    value: cat.value,
    label: cat.label,
  })),
  { value: "CUSTOM", label: "+ Custom Category" },
];

export const SELLABLE_DEVICE_FILTER_COST_TYPE_OPTIONS = [
  { value: "ALL", label: "All Types" },
  { value: "ONE_TIME", label: "One-Time" },
  { value: "RENEWAL", label: "Renewal" },
];

export const SELLABLE_DEVICE_FILTER_STATUS_OPTIONS = [
  { value: "ALL", label: "All Status" },
  { value: "ACTIVE", label: "Active" },
  { value: "INACTIVE", label: "Inactive" },
];

export const SELLABLE_DEVICE_REGION_FILTER_OPTIONS = [
  { value: "ALL", label: "All Regions" },
  ...QUOTATION_REGION_OPTIONS,
];

export const SELLABLE_DEVICE_COLUMNS = [
  { key: "deviceName", label: "Device Name" },
  { key: "deviceCategory", label: "Category" },
  { key: "brandModel", label: "Brand & Model" },
  { key: "regionCurrency", label: "Region" },
  { key: "unitPrice", label: "Unit Price" },
  { key: "costType", label: "Cost Type" },
  { key: "warrantyMonths", label: "Warranty" },
  { key: "status", label: "Status" },
  { key: "actions", label: "Actions" },
];

export const getCategoryMeta = (category?: string) => {
  if (!category) {
    return {
      label: "Other Hardware",
      badgeBg: "#f8fafc",
      badgeColor: "#64748b",
      badgeClass: "sellable-device-badge--other",
    };
  }
  const match = SELLABLE_DEVICE_CATEGORY_OPTIONS.find(
    (c) => c.value.toUpperCase() === category.toUpperCase()
  );
  if (match) return match;
  return {
    label: category,
    badgeBg: "#f1f5f9",
    badgeColor: "#475569",
    badgeClass: "sellable-device-badge--other",
  };
};

export const getCostTypeMeta = (costType?: string) => {
  if (costType === "RENEWAL") {
    return {
      label: "RENEWAL / RECURRING",
      badgeBg: "#fef3c7",
      badgeColor: "#b45309",
      badgeClass: "sellable-device-badge--renewal",
    };
  }
  return {
    label: "ONE-TIME",
    badgeBg: "#dcfce7",
    badgeColor: "#15803d",
    badgeClass: "sellable-device-badge--onetime",
  };
};

export const formatSellablePrice = (price?: number, curency?: string, region?: string): string => {
  const num = typeof price === "number" && !isNaN(price) ? price : 0;
  const curr = curency || (region ? getCurrencyForRegion(region) : "AED");
  return `${curr} ${num.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
};

export const mapSellableCategoryToQuotationCategory = (
  category?: string
): DeviceCategory => {
  if (!category) return "OTHER";
  const catUpper = category.toUpperCase();
  if (catUpper.includes("POS")) return "POS_TERMINAL";
  if (catUpper.includes("PRINTER")) return "PRINTER";
  if (catUpper.includes("SCANNER")) return "BARCODE_SCANNER";
  if (catUpper.includes("KIOSK") || catUpper.includes("KDS")) return "KIOSK";
  if (catUpper.includes("TABLET")) return "TABLET";
  return "OTHER";
};
